import {Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {
  NetscopeApiService,
  Success,
  Response,
  ReportType
} from "@app/services/netscope-api.service";
import {TranslocoService} from "@ngneat/transloco";
import {ReportsData, DownloadReportData} from "@app/netscope/model/data-models";

@Component({
    standalone: false,
  selector: "app-netscope-reports",
  templateUrl: "./netscope-reports.component.html",
  styleUrls: ["./netscope-reports.component.css"],
})
export class NetscopeReportsComponent implements OnInit, OnDestroy {
  @Output()
  loadingStateEmitter = new EventEmitter<boolean>();
  @Output()
  errorStateEmitter = new EventEmitter<boolean>();

  failureMode = false;
  isLoading = false;

  openGenerateReportModal = false;
  duration_type = "multiple_days";
  email_send = "no";
  start_date: Date = undefined;
  end_date: Date = undefined;
  report_type: ReportType = "network";
  // Apps groups files the apps groups report can cover, and the ones it will: the user's files at first, the
  // generated ones being much larger
  appsGroupsFiles: string[] = [];
  selectedAppsGroupsFiles: Set<string> = new Set();

  reportsData: ReportsData;
  intervalRefreshReports;

  constructor(
    private netscopeService: NetscopeApiService,
    public translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    this.intervalRefreshReports = setInterval(() => {
      this.reloadData();
    }, 5000);
    this.reloadData();
  }

  ngOnDestroy(): void {
    if (this.intervalRefreshReports != undefined) {
      clearInterval(this.intervalRefreshReports);
    }
  }

  reloadData = () => {
    this.isLoading = true;
    this.loadingStateEmitter.emit(true);

    this.netscopeService.getReportsData().subscribe((reportsDataResponse) => {
      let reportsData: ReportsData;
      if (reportsDataResponse instanceof Success) {
        reportsData = reportsDataResponse.element;
      } else {
        this.failureMode = true;
        this.errorStateEmitter.emit(true);
        this.isLoading = false;
        this.loadingStateEmitter.emit(false);
        return;
      }

      this.reportsData = reportsData;

      setTimeout(() => {
        this.isLoading = false;
        this.loadingStateEmitter.emit(false);
      }, 300);
    });
  };

  openModal = () => {
    this.openGenerateReportModal = true;
    this.netscopeService.getAppsGroupsFiles().subscribe((response) => {
      if (response instanceof Success) {
        this.appsGroupsFiles = response.element.files;
        this.selectedAppsGroupsFiles = new Set(response.element.files
          .filter((file) => !response.element.generatedFiles.includes(file)));
      }
    });
  };

  toggleAppsGroupsFile = (file: string) => {
    if (this.selectedAppsGroupsFiles.has(file)) {
      this.selectedAppsGroupsFiles.delete(file);
    } else {
      this.selectedAppsGroupsFiles.add(file);
    }
  };

  get canGenerate(): boolean {
    if (this.start_date == undefined || (this.duration_type == "multiple_days" && this.end_date == undefined)) {
      return false;
    }
    return this.report_type != "apps_groups" || this.selectedAppsGroupsFiles.size > 0;
  }

  generateReport = () => {
    let startDate = this.start_date.getTime() / 1000.0;
    let endDate = this.end_date != undefined ? this.end_date.getTime() / 1000.0 : startDate;
    let sendByEmail: boolean = this.email_send == "yes";
    // Kept in the order the files are listed in
    let appsGroupsFiles = this.report_type == "apps_groups"
      ? this.appsGroupsFiles.filter((file) => this.selectedAppsGroupsFiles.has(file))
      : undefined;

    this.netscopeService
      .requestReportGeneration(startDate, endDate, sendByEmail, this.report_type, appsGroupsFiles)
      .subscribe((reportGenerationResponse) => {
        console.log("Report generation request made");
        this.reloadData();
      });
  };

  deleteReport = (reportUuid: string) => {
    this.netscopeService.requestReportDeletion(reportUuid).subscribe((response: Response<DownloadReportData>) => {
      this.reloadData();
    })
  }

  closeModal = () => {
    this.openGenerateReportModal = false;
    this.duration_type = "single_day";
    this.email_send = "no";
    this.start_date = undefined;
    this.end_date = undefined;
    this.report_type = "network";
  };

  downloadReport = (reportUuid: string) => {
    this.netscopeService
      .requestReportDownload(reportUuid)
      .subscribe((response: Response<DownloadReportData>) => {
        let reportData;
        if (response instanceof Success) {
          reportData = response.element;
        }

        var bytes = new Uint8Array(reportData.result.length);
        for (var i = 0; i < reportData.result.length; i++) {
          bytes[i] = reportData.result[i];
        }

        let exportedFilename = reportData.filename;

        let mimeType = "application/octet-stream";

        if (exportedFilename.endsWith(".pdf")) {
          mimeType = "application/pdf";
        }

        if (exportedFilename.endsWith(".doc")) {
          mimeType = "application/msword";
        }

        if (exportedFilename.endsWith(".docx")) {
          mimeType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
        }

        let blob = new Blob([bytes], {
          type: mimeType,
        });
        // @ts-ignore
        if (navigator.msSaveBlob) {
          // IE 10+
          // @ts-ignore
          navigator.msSaveBlob(blob, exportedFilename);
        } else {
          let link = document.createElement("a");
          if (link.download !== undefined) {
            // feature detection
            // Browsers that support HTML5 download attribute
            let url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", exportedFilename);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }
      });
  };
}
